/**
 * Recuperación de chunks huérfanos tras un despliegue.
 *
 * Las rutas protegidas se cargan con `React.lazy`, así que navegar implica
 * descargar un chunk con hash de contenido. Cuando se publica una versión nueva,
 * los hashes cambian y los anteriores dejan de resolverse: la pestaña que llevaba
 * un rato abierta pide un chunk que ya no existe y el `import()` falla con
 * «Failed to fetch dynamically imported module». Recargar arregla el caso porque
 * trae el `index.html` desplegado, que apunta a los hashes vigentes.
 *
 * La recarga lleva guarda temporal: si el chunk falta de verdad (release ya
 * purgada del servidor) el fallo se repite después de recargar, y sin guarda la
 * pestaña entraría en bucle. Pasada la ventana de enfriamiento se vuelve a
 * permitir, porque un despliegue posterior en la misma sesión es un caso
 * legítimo y no un bucle.
 */

/** Instante de la última recarga automática. Exportado para tests y stories. */
export const CHUNK_RELOAD_MARK_KEY = 'chunk-reload-at';

const RELOAD_COOLDOWN_MS = 10_000;

/** Mensajes con que cada navegador informa de un `import()` dinámico fallido. */
const CHUNK_ERROR_SIGNATURES = [
  'failed to fetch dynamically imported module', // Chromium
  'error loading dynamically imported module', // Firefox
  'importing a module script failed', // Safari
  'chunkloaderror',
];

/**
 * Indirección deliberada para la recarga: jsdom declara `location.reload` no
 * configurable, así que ni `vi.spyOn` ni `vi.stubGlobal` pueden observarla. Todo
 * el módulo recarga a través de aquí para que el test pueda espiar la llamada.
 */
export const pageReloader = {
  reload: () => {
    window.location.reload();
  },
};

function readReloadMark(): number | null {
  try {
    const raw = window.sessionStorage.getItem(CHUNK_RELOAD_MARK_KEY);
    if (!raw) return null;

    const parsed = Number.parseInt(raw, 10);

    return Number.isNaN(parsed) ? null : parsed;
  } catch {
    return null;
  }
}

/** Devuelve `false` si la marca no se pudo persistir; entonces no hay guarda. */
function writeReloadMark(at: number): boolean {
  try {
    window.sessionStorage.setItem(CHUNK_RELOAD_MARK_KEY, String(at));

    return true;
  } catch {
    return false;
  }
}

/** Distingue un chunk que no se pudo descargar de un error de la aplicación. */
export function isChunkLoadError(error: unknown): boolean {
  const text = error instanceof Error ? `${error.name} ${error.message}` : typeof error === 'string' ? error : '';
  const normalized = text.toLowerCase();

  return CHUNK_ERROR_SIGNATURES.some((signature) => normalized.includes(signature));
}

/**
 * Recarga la página si eso puede arreglar el chunk que falta. Devuelve si la
 * recarga se ha lanzado, para que el llamante decida qué mostrar mientras.
 */
export function attemptChunkReload(): boolean {
  // Sin red la recarga cambiaría un área de contenido con error por la pantalla
  // de error del navegador: es peor que quedarse.
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return false;

  const lastReloadAt = readReloadMark();
  if (lastReloadAt !== null && Date.now() - lastReloadAt <= RELOAD_COOLDOWN_MS) return false;

  // Sin marca persistida no hay nada que corte un segundo intento: preferimos no
  // recargar y ofrecer el fallback con su botón manual.
  if (!writeReloadMark(Date.now())) return false;

  pageReloader.reload();

  return true;
}

/**
 * Cubre los `import()` que fallan fuera de una frontera de ruta: modales y
 * componentes diferidos de cualquier pantalla.
 *
 * No cancela el evento a propósito. Vite lanza el error salvo que se llame a
 * `preventDefault()`, y si se cancela el `import()` resuelve con `undefined`, con
 * lo que `React.lazy` fallaría luego con un error distinto y menos legible. Se
 * deja propagar para que lo recoja la frontera de ruta.
 */
export function registerChunkPreloadRecovery(): () => void {
  const handlePreloadError = () => {
    attemptChunkReload();
  };

  window.addEventListener('vite:preloadError', handlePreloadError);

  return () => {
    window.removeEventListener('vite:preloadError', handlePreloadError);
  };
}
